import React from 'react'
import { Box, Button, CircularProgress, LinearProgress, Tooltip, Typography } from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'

import { DataTable } from '@/components/UI/DataTable'
import SkeletonRow from '@/components/UI/Skeleton'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { usePermissions } from '@/domain/permissions/usePermissions'
import { ErrorPage } from '@/pages/Error'
import { IPrimaryIncome } from '@/types'
import { actionButtonSx } from '../buttonStyles'
import { EmptyContainer, EmptyMessage } from '../EmptyState'
import { primaryIncomeColumns } from './columns'
import { usePrimaryIncome } from './usePrimaryIncome'

interface PrimaryIncomeProps {
  // Opens the page's detect income dialog; detection is shared with the manual pay cycle review.
  onDetect: () => void
  isDetecting: boolean
  detectionInProgress: boolean
}

export const PrimaryIncome: React.FC<PrimaryIncomeProps> = ({
  onDetect,
  isDetecting,
  detectionInProgress,
}) => {
  const { data, isLoading, isError } = usePrimaryIncome()
  const { user } = useAuth0()
  const { hasPermission } = usePermissions(user?.['https://carringtonlabs.io/appRole'])
  // Detection kicks off a job that can change how the customer is assessed, so it sits with the
  // other write actions rather than with viewing.
  const canDetect = hasPermission(PlatformPermission.UpdateUserDetails)

  const Header = (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
      <Typography variant="h6">Primary Income</Typography>
      {canDetect && (
        <Tooltip title="Invoke FHS to detect latest income for user">
          {/* A disabled button fires no hover events, so the span keeps the tooltip working. */}
          <span>
            <Button
              variant="contained"
              onClick={onDetect}
              disabled={isDetecting || detectionInProgress}
              sx={{ ...actionButtonSx, width: '120px' }}
            >
              {isDetecting || detectionInProgress ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Detect Income'
              )}
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  )

  return (
    <>
      {Header}
      {detectionInProgress && (
        <Box role="status" mb={2}>
          <LinearProgress sx={{ borderRadius: '4px' }} />
          <Typography sx={{ fontFamily: 'Inter', fontSize: '13px', color: '#475467', mt: 1 }}>
            Detection requested. Results can take a few minutes to appear.
          </Typography>
        </Box>
      )}
      {isLoading ? (
        <SkeletonRow columns={primaryIncomeColumns} />
      ) : isError ? (
        <ErrorPage
          title="Unable to Fetch Primary Income"
          message="We couldn't retrieve primary income at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      ) : data ? (
        <DataTable<IPrimaryIncome> rows={[data]} columns={primaryIncomeColumns} />
      ) : (
        <EmptyContainer>
          <EmptyMessage>No primary income set for this customer.</EmptyMessage>
        </EmptyContainer>
      )}
    </>
  )
}
